/**
 * UI permission-keys for the `HELP_REQUEST` route (Help Requests / support chat surface).
 *
 * Route name matches the BE CSV catalogue rows (acl-svc `config/ui-permissions.csv`, the
 * `HELP_REQUEST` block), seeded by migrations `20260922100004-seed-help-request-ui-map.ts`,
 * `20260922110003-seed-help-request-ui-map-followup.ts` and
 * `20260923100001-seed-help-request-invite-ui-map-raisers.ts`.
 *
 * These keys shape the UI only — chat-integration-svc authorizes every real call behind
 * `@RequirePermission` plus a participant row check, so a caller who slips past a missing gate
 * here is still refused by the backend. The reason to gate anyway is that an ungated control is a
 * control that 403s: the MR !1279 review found exactly that on the status dropdown, which was
 * rendered for raisers who are not permitted to close a request.
 */
export const HelpRequestAclKeys = {
  /** The list view. */
  listingRead: 'help_requests:listing:read',
  /** A single request's detail pane. */
  detailRead: 'help_requests:detail:read',
  /** The embedded Sendbird chat panel. */
  chat: 'help_requests:chat',
  /** Raise a new request. */
  create: 'help_requests:create',
  /**
   * The DESK view — every thread in the tenant, not just the caller's own.
   * Backed by `help_request:read-all` (PLATFORM_ADMIN / AGENCY). Widens WITHIN a tenant only;
   * RLS remains the outer boundary.
   */
  readAll: 'help_requests:read_all',
  /**
   * The status/lifecycle control. DESK-SIDE ONLY, by an explicit 2026-09-23 product decision:
   * a raiser may NOT close or reopen their own request — only agency-persona team members can.
   * Backed by `help_request:process` (PLATFORM_ADMIN / AGENCY).
   */
  statusUpdate: 'help_requests:status:update',
  /**
   * The "Invite person" control. Available to raisers as well as the desk, by the same
   * 2026-09-23 decision: a raiser MAY invite into their OWN thread. Backed by
   * `help_request:invite` (PLATFORM_ADMIN / AGENCY / CLIENT / VENDOR — not WORKER).
   *
   * "Their own" is enforced server-side, not here: the backend requires a `help_request_users`
   * row for the thread (or `help_request:read-all`), so holding this key is not by itself reach.
   */
  invite: 'help_requests:invite',

  // ── CI-T17 (2026-09-24): two new DESK capabilities, PLATFORM_ADMIN + AGENCY only. ────────────
  // `moderate` = override a request's service commitments; `configure` = author the tenant's
  // shared help-desk content. Grants: acl-svc `20260924140001-grant-help-request-moderate-
  // configure-to-desk-personas.ts`; UI map: `20260924140002-seed-help-request-moderate-configure-
  // ui-map.ts`.

  /**
   * The SLA due-date / status control (`upsertSlaTaskForHelpRequestV2`).
   * Backed by `help_request:moderate`.
   */
  slaUpdate: 'help_requests:sla:update',
  /**
   * The auto-completion toggle and duration (`updateAutoCompletionConfig`).
   * Backed by `help_request:moderate`. The server additionally requires the desk persona view.
   */
  autoCompletionUpdate: 'help_requests:auto_completion:update',
  /**
   * The chat-question editor (`createQuestionV2` / `updateQuestionV2`).
   * Backed by `help_request:configure`.
   */
  questionsConfigure: 'help_requests:questions:configure',
  /**
   * "Add tag" to the tenant's tag catalogue (`createRequestTagV2`, the GraphQL twin of the
   * subgraph's REST create-tag, which the gateway does not proxy). Backed by
   * `help_request:configure`. Errors: CI_TAG_LABEL_REQUIRED, CI_TAG_DUPLICATE.
   */
  tagsCreate: 'help_requests:tags:create',
  /**
   * The "Retry channel creation" control (`retryHelpRequestChannelProvisioning`, CI-HCR).
   * Backed by `help_request:retry-provisioning` (PLATFORM_ADMIN + AGENCY, PRD D-1). A dedicated
   * key rather than reusing `readAll`: the GraphQL redaction already decides field visibility by
   * agency view, so this key is defense-in-depth against rendering a control that always 403s —
   * not a substitute for the server-side verb check.
   *
   * ui_key spelling (`retry_provisioning`, not `channel_retry`) matches acl-svc's own
   * `ui_permission_map` row exactly — BE already shipped the grant + UI-map seed for
   * PLATFORM_ADMIN + AGENCY (migrations 20261023190001/20261023190002, their own "HCR-T01",
   * unrelated to this workflow's FE-T01) before this key was added here. No CSV scaffold needed:
   * the row already exists, already granted.
   */
  channelRetry: 'help_requests:retry_provisioning',
} as const;

export type HelpRequestAclKey =
  (typeof HelpRequestAclKeys)[keyof typeof HelpRequestAclKeys];
